import { useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import { Table } from 'components/core-ui/Table'
import { getColumns, getFilters, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { DownloadIcon, EditActionIcon } from 'icons'
import { formatAndDownloadResponse } from 'utils/helpers'
import s from '../../../commonStyles.modules.scss'
import {
  getHubOptions,
  getParentSocietyOptions,
  getScreenPermissions,
  getShopOptions,
  getSupportTypeOptionsById
} from '../../api'
import { EditPackagingInventory } from './EditPackagingInventory'
import { History } from '@mui/icons-material'
import { SCREENS, URLS } from '../../constants'
import { useNavigate } from 'react-router-dom'

const PackagingInventory = () => {
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<any>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isCSVExportLoading, setIsCSVExportLoading] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const { data: shopOptions } = getShopOptions(showNotification)
  const { data: hubOptions } = getHubOptions(showNotification)
  const { data: supportTypeOptions } = getSupportTypeOptionsById(showNotification)
  const { data: parentSocietyOptions } = getParentSocietyOptions(showNotification)

  const { data: permissions } = getScreenPermissions(
    showNotification,
    SCREENS.PACKAGING_SUPPORT_STOCK_INVENTORY
  )

  const filters = useMemo(
    () => getFilters({ shopOptions, hubOptions, supportTypeOptions, parentSocietyOptions }),
    [shopOptions, hubOptions, supportTypeOptions, parentSocietyOptions]
  )

  const downloadImage = async id => {
    try {
      const response = await makeCoreGetFileRequest(`/shop-reception/pictures/export/${id}`, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea imaginii. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const columns = useMemo(() => getColumns(downloadImage), [])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/support-inventory',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )
      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const exportFileCSV = async (tableState, e) => {
    setIsCSVExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: '/shop-reception/support-inventories/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response);
      setIsCSVExportLoading(false)
    } catch (error) {
      setIsCSVExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const toolbar = useMemo(() => toolbarActions({
      totalCount,
      exportFileCSV: exportFileCSV,
      isCSVExportLoading }),
    [totalCount, isCSVExportLoading])

  const openModal = () => setIsEditModalOpen(true)
  const closeModal = () => setIsEditModalOpen(false)

  const selectRow = (rowId: number) => {
    const row = tableItems.find(item => item.id === rowId)
    setSelectedRow(row)
    openModal()
  }

  const displayHistory = (rowId: number) =>
    navigate(URLS.AUTH_PACKING_INVENTORY_HISTORY.replace(':id', `${rowId}`))

  return (
    <div className={s.mainContainer}>
      <EditPackagingInventory
        isModalOpen={isEditModalOpen}
        closeModal={closeModal}
        editItem={selectedRow}
        setShouldRefreshData={setShouldRefreshData}
      />
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={
          permissions?.canEdit
            ? [
                {
                  label: 'Editeaza',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#16a085',
                  desktopIcon: <EditActionIcon />,
                  onClick: (_e, rowId: number) => selectRow(rowId)
                },
                {
                  label: 'Date istorice',
                  labelColor: 'white',
                  typographyVariant: 'medium',
                  backgroundColor: '#d9534f',
                  desktopIcon: <History htmlColor="rgb(9, 112, 230)" />,
                  onClick: (_e, rowId: number) => displayHistory(rowId)
                }
              ]
            : []
        }
        rowActionsWidth="130px"
        toolbarActions={
          permissions?.canDownload
            ? toolbar
            : []
        }
        filters={filters}
        filtersButtonLabel="Filtre"
        filtersLabel="Filtre"
        appliedFiltersLabel="Filtre aplicate"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default PackagingInventory
